<script setup lang="ts">
import type { DisplayType } from '@optionway/models';

const prefix = 'components.results.leg';

const isLg = useMediaQuery('(min-width: 1024px)');
const { locale } = useI18n();

defineProps<{
  loading?: boolean;
  dateTime?: string;
  iataCode?: string;
  cityLabel?: string;
  airportLabel?: string;
  displayType?: DisplayType;
  dayOffset?: number;
  compact?: boolean;
  hideTooltips?: boolean;
}>();
</script>

<template>
  <div class="flex flex-col gap-1">
    <div
      v-if="loading"
      class="flex flex-col items-center gap-1"
    >
      <UiSkeleton class="h-6 w-14" />
      <UiSkeleton class="h-5 w-10" />
    </div>

    <template v-else>
      <div
        v-if="dayOffset"
        class="flex flex-col items-end justify-center @lg:flex-row @lg:items-start"
      >
        <UiTooltip :disabled="hideTooltips">
          <UiTooltipTrigger as-child>
            <span
              class="text-xs font-normal text-red-500 @lg:order-2"
              :aria-label="
                $t(
                  `${prefix}.arrivalNextDay`,
                  { arrivalDate: useDateFormat(dateTime, 'ddd D MMM', { locales: locale }).value },
                  dayOffset,
                )
              "
              >+{{ dayOffset }}</span
            >
          </UiTooltipTrigger>
          <UiTooltipContent v-if="isLg">
            {{
              $t(
                `${prefix}.arrivalNextDay`,
                { arrivalDate: useDateFormat(dateTime, 'ddd D MMM', { locales: locale }).value },
                dayOffset,
              )
            }}
          </UiTooltipContent>
        </UiTooltip>
        <span
          class="inline-block font-semibold leading-tight text-text-primary-900"
          :class="compact ? 'text-base' : 'text-lg'"
        >
          {{ useDateFormat(dateTime, 'HH:mm') }}
        </span>
      </div>
      <span
        v-else
        class="inline-block font-semibold leading-tight text-text-primary-900"
        :class="compact ? 'text-base' : 'text-lg'"
      >
        {{ useDateFormat(dateTime, 'HH:mm') }}
      </span>
      <div
        class="text-center text-base font-normal leading-5 text-text-primary-600"
        :class="{
          'text-orange-700': displayType === 'WARNING',
          'text-light-blue-700': displayType === 'INFO',
        }"
      >
        <UiTooltip :disabled="hideTooltips">
          <UiTooltipTrigger as-child>
            <span :aria-label="cityLabel && airportLabel ? `${cityLabel}, ${airportLabel} (${iataCode})` : iataCode">{{
              iataCode
            }}</span>
          </UiTooltipTrigger>
          <UiTooltipContent v-if="isLg"> {{ cityLabel }}, {{ airportLabel }} </UiTooltipContent>
        </UiTooltip>
      </div>
    </template>
  </div>
</template>
